import * as React from 'react';
import {
    Carousel,
    MediaGalleryContextProvider,
    ThemeProvider,
    useGalleryControls,
    useMediaGalleryContext,
    useTheme
} from '@volkswagen-onehub/components-core';

import {AnimatedAutoScrollButton, ArrowButtons} from './components';
import {
    StyledPagination,
    StyledSlideContainer,
    StyledSliderUiWrapper,
    StyledStageSlideContainer
} from './components/styled.components';
import {BulletPagination} from '../mobile-carousel-v2/bullet-pagination';
import {Slide, SlideProps} from './components/Slide';

import {
    useLoopingAnimation,
    useMediaQueries,
    useRemoveElementAttribute,
    useTouchListeners
} from '../../hooks';
import {
    useActiveSlideHeight,
    useCarouselSwipe,
    useManageInertPanels,
    useStopRotatingOnFocusInsideSlide
} from './hooks';
import {
    arrowLeft,
    arrowRight,
    SliderTrackingEventProps,
    SwipeDirection
} from '../../types';
import {resolveTrackingActionName} from './utils';

export interface StageSliderProps {
    handleSlideChange?: (slideIndex: number) => void;
    heightOverride?: string;
    isAuthorMode?: boolean;
    nextSlideAriaLabel: string;
    onTrackingEventSent: (props: SliderTrackingEventProps) => void;
    paginationAriaLabel: string;
    pauseAriaLabel: string;
    playAriaLabel: string;
    previousSlideAriaLabel: string;
    slides: SlideProps[];
    stageSliderAriaLabel: string;
    timeout: number;
}

const StageSliderElement = ({
    heightOverride,
    isAuthorMode = false,
    nextSlideAriaLabel,
    paginationAriaLabel,
    pauseAriaLabel,
    playAriaLabel,
    previousSlideAriaLabel,
    onTrackingEventSent,
    slides,
    stageSliderAriaLabel,
    timeout
}: StageSliderProps): React.ReactElement => {
    const {changeSlide, slideToNextSlide, slideToPreviousSlide} =
        useGalleryControls();
    const {
        state: {activeIndex}
    } = useMediaGalleryContext();
    const {direction} = useTheme();
    const isMobile = useMediaQueries()?.isMobile ?? false;

    const [localActiveIndex, setLocalActiveIndex] = React.useState(0);
    const [isRotating, setIsRotating] = React.useState(!isAuthorMode);

    const carouselRef = React.useRef<null | HTMLDivElement>(null);
    const containerRef = React.useRef<null | HTMLDivElement>(null);
    const rotationBtnRef = React.useRef<null | HTMLDivElement>(null);

    const isRtl = direction === 'rtl';
    const isMobileAndRtl = isMobile && isRtl;

    /**
     * generic info needed in each tracking event
     */
    const trackingData = React.useMemo(() => {
        const currentSlide = slides[localActiveIndex];
        const headingCondition = currentSlide && currentSlide.headingLabel;
        const headingLabel = headingCondition ? currentSlide.headingLabel : '';
        const index = localActiveIndex;
        const itemCount = slides.length;
        const itemPosition = localActiveIndex + 1;

        return {headingLabel, index, itemCount, itemPosition};
    }, [localActiveIndex, slides]);

    const memoizedSlides = React.useMemo(
        () =>
            slides.map((slide, index) => (
                <Slide
                    {...slide}
                    key={`stage-slide-key-${index}`}
                    ariaLabelledBy={`stage-slide-bullet-${index}`}
                    id={`stage-slide-panel-${index}`}
                />
            )),
        [slides]
    );

    const paginationData = React.useMemo(
        () =>
            slides.map(({headingLabel}, idx) => ({
                id: `stage-slide-bullet-${idx}`,
                label: <span>{headingLabel}</span>,
                panelId: `stage-slide-panel-${idx}`
            })),
        [slides]
    );

    const updateLocalActiveIndex = React.useCallback(
        (direction: 'next' | 'prev') => {
            setLocalActiveIndex(prevIndex => {
                const lastListItem = memoizedSlides.length - 1;
                if (direction === 'next') {
                    return prevIndex === lastListItem ? 0 : prevIndex + 1;
                } else {
                    return prevIndex === 0 ? lastListItem : prevIndex - 1;
                }
            });
        },
        [memoizedSlides]
    );

    const handleAnimationComplete = React.useCallback(() => {
        const trackingConfig = {
            loadType: 'automatic',
            ...trackingData
        } as SliderTrackingEventProps;

        onTrackingEventSent(trackingConfig);
        updateLocalActiveIndex('next');
    }, [onTrackingEventSent, trackingData, updateLocalActiveIndex]);

    const handleNextClick = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            slideToNextSlide(event);
            onTrackingEventSent({
                ...trackingData,
                actionName: resolveTrackingActionName(isRtl, arrowRight),
                loadType: 'manual'
            });
            updateLocalActiveIndex('next');
        },
        [
            isRtl,
            onTrackingEventSent,
            slideToNextSlide,
            trackingData,
            updateLocalActiveIndex
        ]
    );

    const handlePrevClick = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            slideToPreviousSlide(event);
            onTrackingEventSent({
                ...trackingData,
                actionName: resolveTrackingActionName(isRtl, arrowLeft),
                loadType: 'manual'
            });
            updateLocalActiveIndex('prev');
        },
        [
            isRtl,
            onTrackingEventSent,
            slideToPreviousSlide,
            trackingData,
            updateLocalActiveIndex
        ]
    );

    const handleSlideSelect = React.useCallback(
        (idx: number) => {
            changeSlide(idx);
            onTrackingEventSent({
                ...trackingData,
                actionName: 'panel-item',
                loadType: 'manual'
            });
            setLocalActiveIndex(idx);
        },
        [onTrackingEventSent, changeSlide, trackingData]
    );

    const handleSwipeSuccess = ({
        swipeDirection
    }: {
        swipeDirection: SwipeDirection;
    }) => {
        if (swipeDirection === 'swipe-left') {
            updateLocalActiveIndex('next');
        } else if (swipeDirection === 'swipe-right') {
            updateLocalActiveIndex('prev');
        } else {
            return;
        }

        onTrackingEventSent({
            ...trackingData,
            actionName: resolveTrackingActionName(isRtl, swipeDirection),
            loadType: 'manual'
        });
    };

    const handleToggleAutoAdvance = () => {
        const trackingConfig = {
            isRotating,
            ...trackingData
        } as SliderTrackingEventProps;

        setIsRotating(prevIsRotating => !prevIsRotating);
        onTrackingEventSent(trackingConfig);
    };

    const {handleCarouselTouchEnd, handleCarouselTouchStart} =
        useCarouselSwipe(handleSwipeSuccess);

    useActiveSlideHeight(containerRef, isMobile, heightOverride);

    useLoopingAnimation(
        'animate',
        isRotating,
        localActiveIndex,
        rotationBtnRef,
        slideToNextSlide,
        timeout,
        handleAnimationComplete
    );

    useRemoveElementAttribute(carouselRef, 'StyledMediaGallery-', 'tabindex');

    useManageInertPanels(carouselRef, localActiveIndex);

    useTouchListeners(
        carouselRef,
        handleCarouselTouchStart,
        handleCarouselTouchEnd
    );

    useStopRotatingOnFocusInsideSlide(carouselRef, setIsRotating);

    return (
        <StyledStageSlideContainer
            role="region"
            aria-label={stageSliderAriaLabel}
            ref={containerRef}
        >
            <StyledSliderUiWrapper>
                <AnimatedAutoScrollButton
                    handleClick={handleToggleAutoAdvance}
                    isRotating={isRotating}
                    pauseAriaLabel={pauseAriaLabel}
                    playAriaLabel={playAriaLabel}
                    ref={rotationBtnRef}
                    rotationBtnAriaLabel={stageSliderAriaLabel}
                    timeout={timeout}
                    isMobileAndRtl={isMobileAndRtl}
                />
                <StyledPagination>
                    <ThemeProvider theme="inverted">
                        <BulletPagination
                            activeIndex={activeIndex}
                            paginationData={paginationData}
                            paginationLabel={paginationAriaLabel}
                            onSlideSelect={handleSlideSelect}
                            enableRtl={isRtl}
                        />
                    </ThemeProvider>
                </StyledPagination>
                <ArrowButtons
                    nextSlideAriaLabel={nextSlideAriaLabel}
                    onClickNext={handleNextClick}
                    onPrevClick={handlePrevClick}
                    previousSlideAriaLabel={previousSlideAriaLabel}
                    isRtl={isRtl}
                />
            </StyledSliderUiWrapper>
            <StyledSlideContainer
                data-test-id="transition-wrapper"
                ref={carouselRef}
            >
                <Carousel>{memoizedSlides}</Carousel>
            </StyledSlideContainer>
        </StyledStageSlideContainer>
    );
};

export const StageSlider = React.memo(
    (props: StageSliderProps): React.ReactElement => (
        <MediaGalleryContextProvider>
            <StageSliderElement {...props} />
        </MediaGalleryContextProvider>
    )
);
